import ApolloClient from 'apollo-boost'
import { getGlobal } from 'reactn'
import { API_TOKEN, API_URL } from 'react-native-dotenv'
import Observable from 'zen-observable'
import { signInWithRefreshToken } from './auth/signInWithRefreshToken'
import { logout } from '../utils/logout'
import { showErrorSnackbar } from '../components/snackbar/Snackbar'
import { formatMessage } from '../constants/strings/intl/intlHooks'
import { messages } from '../constants/strings/messages'
import { setGlobalAndPersist } from '../global/useGlobal'

export let apolloClient: ApolloClient<any>

export const getRequestAuthToken = (tokenFromGlobal: string) => {
  return tokenFromGlobal ? `Bearer ${tokenFromGlobal}` : API_TOKEN
}

export const initializeApolloClient = () => {
  apolloClient = new ApolloClient({
    uri: API_URL,
    onError(input) {
      const operation = input.operation
      const refreshToken = getGlobal().refreshToken
      if (input.graphQLErrors) {
        console.warn('GraphQL erros in ApolloClient', input.graphQLErrors)
        for (let err of input.graphQLErrors) {
          switch (err.extensions.code) {
            case 'UNAUTHORIZED':
              console.log(
                'Received unauthorized response, attempting to refresh access token. Operation:',
                operation.operationName
              )
              if (!refreshToken) {
                console.log('Refresh token not found, new login required')
                handleRequireLogin()
                return
              }
              const refreshAccessToken = async () => {
                await setGlobalAndPersist({ authToken: null })
                return signInWithRefreshToken({ refreshToken }, apolloClient)
              }
              if (!refreshTokenPromise) {
                refreshTokenPromise = refreshAccessToken().then(response => {
                  refreshTokenPromise = undefined
                  return response
                })
              }
              return observableFromPromise(() => refreshTokenPromise).flatMap(() => {
                const authToken = getGlobal().authToken
                if (!authToken) {
                  handleRequireLogin()
                  return
                }
                const oldHeaders = operation.getContext().headers
                operation.setContext({
                  headers: {
                    ...oldHeaders,
                    authorization: getRequestAuthToken(authToken),
                  },
                })
                return input.forward(operation) as any
              })
            case 'FORBIDDEN': {
              console.log('Received forbidden response, new login required.')
              handleRequireLogin()
            }
          }
        }
      }
      if (input.networkError) {
        console.warn(input.networkError)
      }
    },
    request: async operation => {
      const authToken = getGlobal().authToken
      operation.setContext({
        headers: {
          Authorization: getRequestAuthToken(authToken),
        },
      })
    },
  })
  apolloClient.defaultOptions.query = {
    fetchPolicy: 'no-cache',
  }
}

let refreshTokenPromise: Promise<any>

const handleRequireLogin = () => {
  showErrorSnackbar(formatMessage(messages.logoutPrompt))
  logout()
}

const observableFromPromise = (promiseGenerator: () => Promise<any>) =>
  new Observable(observer => {
    promiseGenerator()
      .then(value => {
        observer.next(value)
        observer.complete()
      })
      .catch(err => {
        observer.error(err)
      })
  })
